import React from 'react';

interface EmojiGifProps {
  emoji: string;
  alt?: string;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fallback?: boolean; // Whether to fallback to text emoji if GIF not found
}

// Emoji to SVG filename mapping (using SVG for cross-browser compatibility)
const EMOJI_TO_SVG: Record<string, string> = {
  // Frequently used emojis - only include ones we have SVG files for
  '⭐': 'star.svg',
  '🚗': 'car.svg',
  '⚡': 'lightning.svg',
  '💰': 'money.svg',
  '🍽️': 'plate.svg',
  '⚠️': 'warning.svg',
};

// Size mapping for consistent sizing
const SIZE_CLASSES = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4', 
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-8 h-8'
};

export const EmojiGif: React.FC<EmojiGifProps> = ({
  emoji,
  alt,
  className = '',
  size = 'md',
  fallback = true
}) => {
  const [gifError, setGifError] = React.useState(false);
  
  const svgFilename = EMOJI_TO_SVG[emoji];
  const svgPath = svgFilename ? `/emoji-gifs/${svgFilename}` : null;
  
  // If no SVG mapping exists or SVG failed to load, show fallback
  if (!svgPath || gifError) {
    if (fallback) {
      return (
        <span 
          className={`inline-block ${SIZE_CLASSES[size]} ${className}`}
          role="img" 
          aria-label={alt || `${emoji} emoji`}
          title={alt || `${emoji} emoji`}
        >
          {emoji}
        </span>
      );
    } else {
      return null;
    }
  }
  
  return (
    <img
      src={svgPath}
      alt={alt || `${emoji} icon`}
      title={alt || `${emoji} icon`}
      className={`inline-block ${SIZE_CLASSES[size]} ${className}`}
      onError={() => setGifError(true)}
      loading="lazy"
      style={{
        objectFit: 'contain',
        verticalAlign: 'middle'
      }}
    />
  );
};

// Utility function to replace emojis in text with SVG components
export const replaceEmojisWithGifs = (text: string, className?: string, size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl') => {
  const emojiRegex = /(⭐|🚗|⚡|💰|🍽️|⚠️)/g;
  
  const parts = text.split(emojiRegex);
  
  return parts.map((part, index) => {
    if (emojiRegex.test(part)) {
      return (
        <EmojiGif 
          key={index}
          emoji={part}
          className={className}
          size={size}
        />
      );
    }
    return part;
  });
};

export default EmojiGif;